import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Inbox, Loader2, LogOut } from "lucide-react";
import { listInquiries, getMe, getToken, clearToken } from "@/lib/api";
import { AdminLogin } from "@/components/AdminLogin";

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await listInquiries();
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearToken();
        setAuthed(false);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setAuthed(false);
        return;
      }
      try {
        await getMe();
        setAuthed(true);
        loadInquiries();
      } catch {
        clearToken();
        setAuthed(false);
      }
    })();
  }, [loadInquiries]);

  const handleLogout = () => {
    clearToken();
    setItems([]);
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center text-[#55524B]">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (authed === false) {
    return (
      <AdminLogin
        onSuccess={() => {
          setAuthed(true);
          loadInquiries();
        }}
      />
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F5F1EA] text-[#0F172A]">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center justify-between border-b border-[#C9C3B4] pb-8 mb-10">
          <div>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#0055FF]">
              / Internal
            </span>
            <h1 className="font-display font-800 tracking-tighter text-3xl md:text-4xl mt-3">
              Quote Inquiries
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-back"
              className="btn-industrial inline-flex items-center gap-2 border border-[#C9C3B4] hover:border-[#0055FF] px-5 py-3 font-mono-tech text-[11px] uppercase tracking-widest text-[#55524B] hover:text-[#0F172A]"
            >
              <ArrowLeft className="h-4 w-4" /> Site
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="btn-industrial inline-flex items-center gap-2 border border-[#C9C3B4] hover:border-[#0055FF] px-5 py-3 font-mono-tech text-[11px] uppercase tracking-widest text-[#55524B] hover:text-[#0F172A]"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#55524B] py-20 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading inquiries…
          </div>
        ) : error ? (
          <p className="text-[#55524B] py-20 text-center">Failed to load inquiries.</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-24 text-[#55524B]" data-testid="admin-empty">
            <Inbox className="h-10 w-10 text-[#C9C3B4]" />
            <p>No inquiries yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-list">
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-[#55524B] mb-4">
              {items.length} total
            </p>
            {items.map((it) => (
              <div
                key={it.id}
                data-testid="admin-inquiry-row"
                className="border border-[#C9C3B4] hover:border-[#0055FF]/50 transition-colors p-6 bg-[#FFFFFF]"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div>
                    <h3 className="font-display font-700 text-lg">
                      {it.name}
                      {it.company ? (
                        <span className="text-[#55524B] font-400"> · {it.company}</span>
                      ) : null}
                    </h3>
                    <p className="text-[#0055FF] text-sm mt-1">{it.email}</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-[#55524B]">
                      {it.phone ? <span>{it.phone}</span> : null}
                      {it.industry ? (
                        <span className="font-mono-tech text-[10px] uppercase tracking-widest border border-[#C9C3B4] px-2 py-0.5">
                          {it.industry}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#9B9689] shrink-0">
                    {it.created_at ? format(new Date(it.created_at), "dd MMM yyyy · HH:mm") : ""}
                  </span>
                </div>
                <p className="text-[#1E293B] text-sm mt-4 whitespace-pre-line leading-relaxed border-t border-[#C9C3B4] pt-4">
                  {it.message}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
